import { Controller, useForm, useFormContext } from "react-hook-form";
import { Field } from "@base-ui/react/field";
import { Form } from "@base-ui/react/form";
import { EntityStatus } from "@repo/types/enums";
import type { AddUpdateTemplateViewModel } from "../../types/viewModels/AddUpdateTemplateViewModel";
import type TextEditorViewModel from "../../types/viewModels/TextEditorViewModel";
import useAddUpdateTemplate from "../../hooks/queries/useTemplates";
import Button from "../ui/button/Button";
import { isQuillContentEmpty } from "../../helpers/quillEmpty";

type AddEditTemplateProps = {
  onClose: () => void;
};

export default function AddEditTemplate({ onClose }: AddEditTemplateProps) {
  const { mutateAsync: updateTemplate } = useAddUpdateTemplate();

  const mainForm = useFormContext<TextEditorViewModel>();

  const {
    control,
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<AddUpdateTemplateViewModel>({
    defaultValues: {
      Id: null,
      Name: "",
      Content: "", // 👈 required here
      Status: EntityStatus.Active,
    },
  });

  async function onSubmit(data: AddUpdateTemplateViewModel) {
    const content = mainForm.getValues("Content");

    if (isQuillContentEmpty(content)) {
      setError("Content", {
        type: "required",
        message: "Template content must not be empty",
      });
      return;
    }

    data.Content = content;

    await updateTemplate(data);
    onClose();
  }

  return (
    <div className="text-gray-600">
      <div className="mb-0">
        <div className="flex border-b border-gray-200 focus:border-blue-400 p-2">
          <div className="flex-1">
            <div className="text-gray-400 text-base mb-3">Save template</div>

            <Form>
              <input type="hidden" {...register("Id")} />
              <input
                type="hidden"
                {...register("Status")}
                value={EntityStatus.Active}
              />

              <div className="">
                <Controller
                  name="Name"
                  control={control}
                  rules={{
                    required: "Template name is required",
                  }}
                  render={({
                    field: { value, onChange, onBlur, ref },
                    fieldState: { invalid, isTouched, isDirty, error },
                  }) => (
                    <Field.Root
                      className="flex w-full flex-col items-start gap-1"
                      name="Name"
                      invalid={!!invalid}
                      touched={isTouched}
                      dirty={isDirty}
                    >
                      <Field.Label className="text-sm text-gray-900">
                        Template name
                      </Field.Label>
                      <Field.Control
                        required
                        placeholder="Template name"
                        type="text"
                        value={value}
                        className="h-10 w-full rounded border border-gray-200 pl-3.5 text-sm text-gray-900 focus:-outline-offset-1 focus:outline-blue-800"
                        onBlur={onBlur}
                        onValueChange={onChange}
                        ref={ref}
                      />
                      <Field.Error
                        className="text-sm text-red-800"
                        match={!!error}
                      >
                        {error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
              </div>

              {errors.Content && (
                <div className="rounded mt-2 mb-2 pt-1 pb-1 text-sm text-red-800">
                  {errors.Content.message}
                </div>
              )}

              <div className="flex justify-end space-x-2 items-center mt-4">
                <Button
                  size="sm"
                  variant="ghost"
                  className="px-4 py-2"
                  onClick={() => onClose()}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  size="sm"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
